<template>
	<div
		class="min-h-screen flex flex-col transition-colors duration-300"
		:class="isLight ? 'bg-[#e4f4f0]' : 'bg-[#0f1f1c]'">

		<NavBarComponent :isLight="isLight" />

		<!-- Imigongo background patterns -->
		<div class="fixed inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
			<svg class="absolute -top-10 -right-10 w-[420px] h-[420px]"
				:style="{ opacity: isLight ? 0.07 : 0.06 }"
				viewBox="0 0 160 160" fill="none" xmlns="http://www.w3.org/2000/svg">
				<g :fill="isLight ? '#0d6b5e' : '#a8d5cc'">
					<rect x="40"  y="0"   width="28" height="28" transform="rotate(45 40  0)"   />
					<rect x="80"  y="0"   width="28" height="28" transform="rotate(45 80  0)"   />
					<rect x="120" y="0"   width="28" height="28" transform="rotate(45 120 0)"   />
					<rect x="20"  y="40"  width="28" height="28" transform="rotate(45 20  40)"  />
					<rect x="60"  y="40"  width="28" height="28" transform="rotate(45 60  40)"  />
					<rect x="100" y="40"  width="28" height="28" transform="rotate(45 100 40)"  />
					<rect x="140" y="40"  width="28" height="28" transform="rotate(45 140 40)"  />
					<rect x="40"  y="80"  width="28" height="28" transform="rotate(45 40  80)"  />
					<rect x="80"  y="80"  width="28" height="28" transform="rotate(45 80  80)"  />
					<rect x="120" y="80"  width="28" height="28" transform="rotate(45 120 80)"  />
					<rect x="20"  y="120" width="20" height="20" transform="rotate(45 20  120)" />
					<rect x="60"  y="120" width="20" height="20" transform="rotate(45 60  120)" />
					<rect x="100" y="120" width="20" height="20" transform="rotate(45 100 120)" />
					<rect x="140" y="120" width="20" height="20" transform="rotate(45 140 120)" />
				</g>
			</svg>
			<svg class="absolute -bottom-10 -left-10 w-[280px] h-[280px]"
				:style="{ opacity: isLight ? 0.06 : 0.05 }"
				viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
				<g :stroke="isLight ? '#0f1f1c' : '#a8d5cc'" stroke-width="3" fill="none">
					<rect x="60" y="10" width="64" height="64" transform="rotate(45 60 10)" />
					<rect x="60" y="22" width="46" height="46" transform="rotate(45 60 22)" />
					<rect x="60" y="34" width="28" height="28" transform="rotate(45 60 34)" />
					<rect x="60" y="46" width="10" height="10" transform="rotate(45 60 46)" />
				</g>
			</svg>
		</div>

		<main class="relative flex-grow pt-24 pb-20 font-['Inter',sans-serif]">
			<div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

				<!-- SearchComponent -->
				<div class="mb-8">
					<SearchComponent :isLight="isLight" />
				</div>

				<!-- ACTIVE EVENTS -->
				<section class="mb-14">
					<div class="flex items-baseline justify-between mb-5">
						<h2 class="font-['Montserrat'] font-bold text-[19px] tracking-tight"
							:class="isLight ? 'text-[#0f1f1c]' : 'text-white'">
							{{ $t('eventsView.activeTitle') }}
							<span class="text-xs font-normal ml-2"
								:class="isLight ? 'text-[#6b8f89]' : 'text-[#a8d5cc]/60'">
								{{ activeEvents.length }} {{ $t('eventsView.happeningNow') }}
							</span>
						</h2>
					</div>
					<div v-if="isLoading" class="flex flex-col items-center justify-center py-16 gap-3">
						<svg class="w-6 h-6 animate-spin text-primary-400" fill="none" viewBox="0 0 24 24">
							<circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
							<path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
						</svg>
						<p class="text-[13px] text-muted dark:text-muted-light" :class="isLight ? 'text-gray-900' : 'text-white'">Loading events…</p>
					</div>
					<div v-if="activeEvents.length === 0 && !isLoading"
						class="flex flex-col items-center justify-center py-16 gap-4">
						<svg class="w-12 h-12" :style="{ opacity: isLight ? 0.2 : 0.15 }" viewBox="0 0 64 64" fill="none">
							<rect x="32" y="2"  width="38" height="38" transform="rotate(45 32 2)"  :fill="isLight ? '#0d6b5e' : '#a8d5cc'" />
							<rect x="32" y="14" width="22" height="22" transform="rotate(45 32 14)" :fill="isLight ? '#e4f4f0' : '#0f1f1c'" />
							<rect x="32" y="24" width="8"  height="8"  transform="rotate(45 32 24)" :fill="isLight ? '#0d6b5e' : '#a8d5cc'" />
						</svg>
						<p class="text-sm" :class="isLight ? 'text-[#0f1f1c]/40' : 'text-white/35'">{{ $t('eventsView.noActive') }}</p>
					</div>

					<div v-else class="grid gap-3.5"
						style="grid-template-columns: repeat(auto-fill, minmax(185px, 1fr))">
						<EventCardComponent
							v-for="event in paginatedActiveEvents"
							:key="event.id"
							:event="event"
							:isLight="isLight" />
					</div>

					<div v-if="activeEvents.length > itemsPerPage"
						class="flex items-center justify-center gap-1.5 mt-6">
						<button
							@click="changePage(activeEventsPage - 1, 'active')"
							:disabled="activeEventsPage === 1"
							class="w-8 h-8 rounded-full border flex items-center justify-center transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed"
							:class="isLight ? 'border-[#1a9b8a]/25 text-[#0d6b5e] hover:bg-[#1a9b8a]/10' : 'border-white/[0.08] text-white/50 hover:bg-white/[0.06]'">
							<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="w-4 h-4">
								<path fill-rule="evenodd" d="M11.78 5.22a.75.75 0 0 1 0 1.06L8.06 10l3.72 3.72a.75.75 0 1 1-1.06 1.06l-4.25-4.25a.75.75 0 0 1 0-1.06l4.25-4.25a.75.75 0 0 1 1.06 0Z" clip-rule="evenodd"/>
							</svg>
						</button>
						<button
							v-for="page in visiblePages('active')"
							:key="page"
							@click="changePage(page, 'active')"
							class="w-8 h-8 rounded-full border text-xs font-semibold transition-all duration-200"
							:class="activeEventsPage === page
								? 'bg-[#1a9b8a] text-white border-[#1a9b8a] shadow-sm shadow-[#1a9b8a]/30'
								: isLight
									? 'border-[#1a9b8a]/20 text-[#0d6b5e] hover:bg-[#1a9b8a]/10'
									: 'border-white/[0.08] text-white/50 hover:bg-white/[0.06]'">
							{{ page }}
						</button>
						<button
							@click="changePage(activeEventsPage + 1, 'active')"
							:disabled="activeEventsPage * itemsPerPage >= activeEvents.length"
							class="w-8 h-8 rounded-full border flex items-center justify-center transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed"
							:class="isLight ? 'border-[#1a9b8a]/25 text-[#0d6b5e] hover:bg-[#1a9b8a]/10' : 'border-white/[0.08] text-white/50 hover:bg-white/[0.06]'">
							<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="w-4 h-4">
								<path fill-rule="evenodd" d="M8.22 5.22a.75.75 0 0 1 1.06 0l4.25 4.25a.75.75 0 0 1 0 1.06l-4.25 4.25a.75.75 0 0 1-1.06-1.06L11.94 10 8.22 6.28a.75.75 0 0 1 0-1.06Z" clip-rule="evenodd"/>
							</svg>
						</button>
					</div>
				</section>

				<!-- Divider -->
				<div class="border-t mb-14" :class="isLight ? 'border-black/[0.06]' : 'border-white/[0.05]'"></div>

				<!-- ARCHIVED EVENTS -->
				<section>
					<div class="flex items-baseline justify-between mb-5">
						<h2 class="font-['Montserrat'] font-bold text-[19px] tracking-tight"
							:class="isLight ? 'text-[#0f1f1c]' : 'text-white'">
							{{ $t('eventsView.archivedTitle') }}
							<span class="text-xs font-normal ml-2"
								:class="isLight ? 'text-[#6b8f89]' : 'text-[#a8d5cc]/60'">
								{{ archivedEvents.length }} {{ $t('eventsView.past') }}
							</span>
						</h2>
					</div>
					<div v-if="isLoading" class="flex flex-col items-center justify-center py-16 gap-3">
						<svg class="w-6 h-6 animate-spin text-primary-400" fill="none" viewBox="0 0 24 24">
							<circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
							<path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
						</svg>
						<p class="text-[13px] text-muted dark:text-muted-light" :class="isLight ? 'text-gray-900' : 'text-white'">Loading events…</p>
					</div>
					<div v-if="archivedEvents.length === 0 && !isLoading"
						class="flex flex-col items-center justify-center py-16 gap-4">
						<svg class="w-12 h-12" :style="{ opacity: isLight ? 0.2 : 0.15 }" viewBox="0 0 64 64" fill="none">
							<rect x="32" y="2"  width="38" height="38" transform="rotate(45 32 2)"  :fill="isLight ? '#0d6b5e' : '#a8d5cc'" />
							<rect x="32" y="14" width="22" height="22" transform="rotate(45 32 14)" :fill="isLight ? '#e4f4f0' : '#0f1f1c'" />
							<rect x="32" y="24" width="8"  height="8"  transform="rotate(45 32 24)" :fill="isLight ? '#0d6b5e' : '#a8d5cc'" />
						</svg>
						<p class="text-sm" :class="isLight ? 'text-[#0f1f1c]/40' : 'text-white/35'">{{ $t('eventsView.noArchived') }}</p>
					</div>

					<div v-else class="grid gap-3.5"
						style="grid-template-columns: repeat(auto-fill, minmax(185px, 1fr))">
						<EventCardComponent
							v-for="event in paginatedArchivedEvents"
							:key="event.id"
							:event="event"
							:isLight="isLight" />
					</div>

					<div v-if="archivedEvents.length > itemsPerPage"
						class="flex items-center justify-center gap-1.5 mt-6">
						<button
							@click="changePage(archivedEventsPage - 1, 'archived')"
							:disabled="archivedEventsPage === 1"
							class="w-8 h-8 rounded-full border flex items-center justify-center transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed"
							:class="isLight ? 'border-[#1a9b8a]/25 text-[#0d6b5e] hover:bg-[#1a9b8a]/10' : 'border-white/[0.08] text-white/50 hover:bg-white/[0.06]'">
							<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="w-4 h-4">
								<path fill-rule="evenodd" d="M11.78 5.22a.75.75 0 0 1 0 1.06L8.06 10l3.72 3.72a.75.75 0 1 1-1.06 1.06l-4.25-4.25a.75.75 0 0 1 0-1.06l4.25-4.25a.75.75 0 0 1 1.06 0Z" clip-rule="evenodd"/>
							</svg>
						</button>
						<button
							v-for="page in visiblePages('archived')"
							:key="page"
							@click="changePage(page, 'archived')"
							class="w-8 h-8 rounded-full border text-xs font-semibold transition-all duration-200"
							:class="archivedEventsPage === page
								? 'bg-[#1a9b8a] text-white border-[#1a9b8a] shadow-sm shadow-[#1a9b8a]/30'
								: isLight
									? 'border-[#1a9b8a]/20 text-[#0d6b5e] hover:bg-[#1a9b8a]/10'
									: 'border-white/[0.08] text-white/50 hover:bg-white/[0.06]'">
							{{ page }}
						</button>
						<button
							@click="changePage(archivedEventsPage + 1, 'archived')"
							:disabled="archivedEventsPage * itemsPerPage >= archivedEvents.length"
							class="w-8 h-8 rounded-full border flex items-center justify-center transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed"
							:class="isLight ? 'border-[#1a9b8a]/25 text-[#0d6b5e] hover:bg-[#1a9b8a]/10' : 'border-white/[0.08] text-white/50 hover:bg-white/[0.06]'">
							<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="w-4 h-4">
								<path fill-rule="evenodd" d="M8.22 5.22a.75.75 0 0 1 1.06 0l4.25 4.25a.75.75 0 0 1 0 1.06l-4.25 4.25a.75.75 0 0 1-1.06-1.06L11.94 10 8.22 6.28a.75.75 0 0 1 0-1.06Z" clip-rule="evenodd"/>
							</svg>
						</button>
					</div>
				</section>
			</div>
		</main>

		<FooterComponent :isLight="isLight" @toggle-mode="toggleMode" />

	</div>
</template>

<script>
import NavBarComponent from "@/components/NavBarComponent.vue";
import FooterComponent from "@/components/FooterComponent.vue";
import EventCardComponent from "@/components/EventCardComponent.vue";
import SearchComponent from "@/components/SearchComponent.vue";
import { useTheme } from "@/composables/useTheme";
import apiService from "@/services/api";

export default {
	components: {
		NavBarComponent,
		FooterComponent,
		EventCardComponent,
		SearchComponent,
  },
  setup() {
        const { isLight, toggleMode } = useTheme();
        return { isLight, toggleMode };
    },
	data() {
		return {
			events: [],
			activeEvents: [],
			archivedEvents: [],
			activeEventsPage: 1,
			archivedEventsPage: 1,
			itemsPerPage: 12,
			paginatedActiveEvents: [],
			paginatedArchivedEvents: [],
			isLoading: false,
		};
	},
	created() {
		this.fetchEvents();
	},
	methods: {
		async fetchEvents() {
			try {
				this.isLoading = true;
				const response = await apiService.getEvents();

				if (response.data && Array.isArray(response.data.data)) {

					const currentDate = new Date();
					this.activeEvents = [];
					this.archivedEvents = [];

					this.events = response.data.data.map((item) => {
						const eventDate = new Date(item.end_date);
						const eventTime = item.end_time.split(":");

						eventDate.setHours(eventTime[0]);
						eventDate.setMinutes(eventTime[1]);
						eventDate.setSeconds(0);
						eventDate.setMilliseconds(0);

						const statusObj = this.getEventStatus(item);
						const statusMap = {
							"LIVE":          "live",
							"STARTING SOON": "soon",
							"RSVP":          "rsvp",
							"ENDED":         "ended",
						};
						const cardStatus = statusMap[statusObj.status] || "rsvp";

						const titleStr = item.title || "Untitled Event";
						const priceNum = parseFloat(item.tickets?.[0]?.Amount) || 0;

						const event = {
							// camelCase (original)
							id:          item.event_id,
							title:       titleStr,
							location:    item.location,
							date:        item.date,
							endDate:     item.end_date,
							endTime:     item.end_time,
							startTime:   item.start_time,
							description: item.description,
							imageUrl:    item.image_url,
							organizer:   item.organizer,
							capacity:    item.capacity,
							category:    item.categories,
							eventType:   item.EventType,
							longitude:   item.longitude,
							latitude:    item.latitude,
							location_name: item.location_name,
							organizerLogos: Array.isArray(item.organizer_logos)
								? item.organizer_logos.map((logo) => ({
									url: logo.LogoURL || "https://www.adaptivewfs.com/wp-content/uploads/2020/07/logo-placeholder-image.png",
								}))
								: [],
							// PascalCase for EventCardComponent
							Title:        titleStr,
							ImageURL:     item.image_url || null,
							posterGhost:  titleStr.toUpperCase(),
							Description:  item.description || "",
							venue:        item.location || "",
							price:        priceNum === 0 ? this.$t('common.free') : `$${priceNum.toFixed(2)}`,
							priceSub:     item.tickets?.[0]?.Type || "",
							tags:         Array.isArray(item.categories)
								? item.categories.map((c) => `#${c.Name || c.name || c}`)
								: [],
							status:       cardStatus,
							attendees:    [],
							attendeeCount: "",
							saved:        false,
						};

						if (eventDate >= currentDate) {
							this.activeEvents.push(event);
						} else {
							this.archivedEvents.push(event);
						}

						return event;
					});

					this.updatePaginatedEvents();
				} else {
					console.error("Invalid response from the API");
					this.events = [];
					this.activeEvents = [];
					this.archivedEvents = [];
				}
			} catch (error) {
				console.error(error);
			} finally {
				this.isLoading = false;
			}
		},

		updatePaginatedEvents() {
			this.paginatedActiveEvents = this.activeEvents.slice(
				(this.activeEventsPage - 1) * this.itemsPerPage,
				this.activeEventsPage * this.itemsPerPage
			);
			this.paginatedArchivedEvents = this.archivedEvents.slice(
				(this.archivedEventsPage - 1) * this.itemsPerPage,
				this.archivedEventsPage * this.itemsPerPage
			);
		},

		changePage(page, type) {
			if (type === "active") {
				this.activeEventsPage = page;
				this.updatePaginatedEvents();
			} else if (type === "archived") {
				this.archivedEventsPage = page;
				this.updatePaginatedEvents();
			}
		},

		visiblePages(type) {
			const totalPages =
				type === "active"
					? Math.ceil(this.activeEvents.length / this.itemsPerPage)
					: Math.ceil(this.archivedEvents.length / this.itemsPerPage);
			const currentPage =
				type === "active" ? this.activeEventsPage : this.archivedEventsPage;
			const pages = [];
			for (let i = 1; i <= totalPages; i++) {
				if (
					i === 1 ||
					i === totalPages ||
					(i >= currentPage - 2 && i <= currentPage + 2)
				) {
					pages.push(i);
				}
			}
			return pages;
		},

		getEventStatus(event) {
			const now = new Date();
			const eventStartDate = new Date(event.date);
			const eventEndDate = new Date(event.end_date);

			if (isNaN(eventStartDate) || isNaN(eventEndDate)) {
				return { status: "Invalid Date", color: "bg-gray-500" };
			}

			let startTime = event.start_time ? event.start_time.split(":") : [];
			if (startTime.length === 2) {
				eventStartDate.setHours(startTime[0]);
				eventStartDate.setMinutes(startTime[1]);
				eventStartDate.setSeconds(0);
				eventStartDate.setMilliseconds(0);
			} else {
				return { status: "Invalid Time", color: "bg-gray-500" };
			}

			let endTime = event.end_time ? event.end_time.split(":") : [];
			if (endTime.length === 2) {
				eventEndDate.setHours(endTime[0]);
				eventEndDate.setMinutes(endTime[1]);
				eventEndDate.setSeconds(0);
				eventEndDate.setMilliseconds(0);
			} else {
				return { status: "Invalid Time", color: "bg-gray-500" };
			}

			if (now < eventStartDate && eventStartDate - now > 24 * 60 * 60 * 1000) {
				return { status: "RSVP", color: "bg-blue-500" };
			}
			if (now >= eventStartDate && now <= eventEndDate) {
				return { status: "LIVE", color: "bg-green-500" };
			}
			if (now > eventEndDate) {
				return { status: "ENDED", color: "bg-red-500" };
			}
			if (now < eventStartDate && eventStartDate - now <= 24 * 60 * 60 * 1000) {
				return { status: "STARTING SOON", color: "bg-yellow-500" };
			}

			return { status: "Status Unknown", color: "bg-gray-500" };
		},

		viewEventDetails(id) {
			this.$router.push({ name: "EventDetails", params: { id } });
		},
	},
};
</script>

<style>
@import url("https://fonts.googleapis.com/css2?family=Montserrat:wght@400;600;700;800&family=Inter:wght@300;400;500&display=swap");
</style>